/**
 * Sanitized SIP WebSocket diagnostics: why the phone is not connected, in a form that is safe to show,
 * log and attach to evidence.
 *
 * Only these facts leave this module: the endpoint as scheme, host, port and path (path segments that
 * look like tokens are masked); attempt numbers; start, open and end times; WebSocket close code,
 * filtered close reason and wasClean; whether an error event fired; REGISTER status codes; and
 * `securitypolicyviolation` reports for connect-src. Never credentials, SIP Authorization headers or
 * any other SIP message content, SDP, cookies or tokens: the adapter hands this module flags and
 * numbers only, and the engine never passes the bootstrap password or the AOR here.
 */
import type { SipFailure } from './sip.js';

export interface TransportEndpoint {
  readonly scheme: 'wss' | 'ws';
  readonly host: string;
  /** Explicit, also when the URL used the scheme default (443 for wss, 80 for ws). */
  readonly port: number;
  readonly path: string;
}

export interface TransportClose {
  readonly code: number;
  /** Server-supplied close reason, filtered to printable ASCII with addresses and token-like strings removed. */
  readonly reason: string;
  readonly was_clean: boolean;
}

export interface TransportCspViolation {
  readonly directive: 'connect-src';
  readonly blocked: TransportEndpoint | null;
  readonly disposition: 'enforce' | 'report';
}

/**
 * One observation from the SIP transport adapter. `socket` is an adapter-local sequence number that ties
 * late events (a close that arrives after the engine already handled the failure) to their connection.
 */
export type SipSocketObservation =
  | { readonly kind: 'socket_created'; readonly socket: number }
  | { readonly kind: 'socket_not_created'; readonly socket: number }
  | { readonly kind: 'socket_open'; readonly socket: number; readonly protocol: string }
  | { readonly kind: 'socket_error'; readonly socket: number }
  | { readonly kind: 'socket_closed'; readonly socket: number; readonly code: number; readonly reason: string; readonly was_clean: boolean }
  | { readonly kind: 'connect_timeout'; readonly socket: number }
  | { readonly kind: 'csp_violation'; readonly blocked: TransportEndpoint | null; readonly disposition: 'enforce' | 'report' };

/**
 * Why an attempt failed. `handshake_failed` and `connection_lost` are inferences: browsers report every
 * failed WebSocket handshake (DNS, TCP, TLS, HTTP status, subprotocol, private-network policy) as close
 * code 1006 with no reason, so the page cannot tell those apart.
 */
export type TransportFailureKind =
  | 'socket_not_created'
  | 'blocked_by_csp'
  | 'connect_timeout'
  | 'handshake_failed'
  | 'closed_by_server'
  | 'connection_lost'
  | 'register_rejected'
  | 'register_timeout'
  | 'register_failed'
  | 'transport_error';

export const OBSERVED_FAILURES: ReadonlySet<TransportFailureKind> = new Set(['socket_not_created', 'blocked_by_csp', 'connect_timeout', 'closed_by_server', 'register_rejected', 'register_timeout']);

export type TransportEventKind =
  | 'connect_started'
  | 'register_started'
  | 'socket_not_created'
  | 'socket_open'
  | 'socket_error'
  | 'socket_closed'
  | 'connect_timeout'
  | 'csp_violation'
  | 'registered'
  | 'attempt_failed'
  | 'retry_scheduled';

/** One connect (or re-register on an open socket) attempt. */
export interface TransportAttempt {
  /** 1 for the first try after start, a registration, "Retry now" or coming back online; then 2, 3, ... */
  readonly attempt: number;
  /** `register`: the WebSocket was already open and only REGISTER was retried. */
  readonly via: 'connect' | 'register';
  readonly endpoint: TransportEndpoint | null;
  readonly started_at: string;
  readonly opened_at: string | null;
  /** When the attempt reached its latest outcome (registered or failed). */
  readonly ended_at: string | null;
  /** Start until the socket opened, or until it closed without opening. */
  readonly connect_ms: number | null;
  readonly outcome: 'pending' | 'open' | 'registered' | 'failed';
  /** False when the browser refused to construct the WebSocket at all; null when unknown (no adapter report). */
  readonly socket_created: boolean | null;
  readonly error_seen: boolean;
  readonly timed_out: boolean;
  readonly close: TransportClose | null;
  readonly csp_violation: TransportCspViolation | null;
  readonly sip_status: number | null;
  readonly failure: TransportFailureKind | null;
  /** The engine's own failure code (for example `transport_connect_failed`, `register_rejected`). */
  readonly failure_code: string | null;
}

export interface TransportLogEntry {
  /** Monotonic for the engine's lifetime. */
  readonly sequence: number;
  readonly at: string;
  readonly kind: TransportEventKind;
  readonly attempt: number;
  readonly failure: TransportFailureKind | null;
  readonly close: TransportClose | null;
  readonly sip_status: number | null;
  readonly connect_ms: number | null;
  readonly retry_in_ms: number | null;
}

export interface TransportDiagnostic {
  readonly endpoint: TransportEndpoint | null;
  readonly current: TransportAttempt | null;
  /** The most recent failed attempt. Kept after later attempts start, so the reason stays visible. */
  readonly last_failure: TransportAttempt | null;
  /** Failed attempts since the last successful registration. */
  readonly consecutive_failures: number;
  readonly next_retry_at: string | null;
  readonly retry_delay_ms: number | null;
  /** Bounded, oldest first. */
  readonly recent: readonly TransportLogEntry[];
}

export const TRANSPORT_LOG_LIMIT = 40;
/** Browsers queue `securitypolicyviolation`, so it can arrive after the close; it still belongs to that attempt. */
const LATE_CSP_MS = 5000;
const KEEP_ATTEMPTS = 4;

const HOST = /^(?:[a-z0-9-]{1,63}\.)*[a-z0-9-]{1,63}$|^\[[0-9a-f:.]{2,45}\]$/;
const SEGMENT = /^[A-Za-z0-9._~-]{1,40}$/;
/** Long opaque segments, or 16+ characters mixing letters and digits: possibly a token or an ID, so masked. */
const TOKENISH = /^[A-Za-z0-9._~-]{24,}$|^(?=[^/]*\d)(?=[^/]*[A-Za-z])[A-Za-z0-9_-]{16,}$/;

/** Scheme, host, port and path of a ws/wss URL. User info, query and fragment are dropped; token-like path segments are masked. */
export function transportEndpoint(url: string | null | undefined): TransportEndpoint | null {
  if (typeof url !== 'string' || url.length > 2048) return null;
  let parsed: URL;
  try { parsed = new URL(url); } catch { return null; }
  const scheme = parsed.protocol === 'wss:' ? 'wss' : parsed.protocol === 'ws:' ? 'ws' : null;
  const host = parsed.hostname.toLowerCase();
  if (!scheme || !HOST.test(host)) return null;
  const port = parsed.port ? Number(parsed.port) : scheme === 'wss' ? 443 : 80;
  const segments = parsed.pathname.split('/').filter(Boolean).slice(0, 8).map(s => (SEGMENT.test(s) && !TOKENISH.test(s) ? s : ':masked'));
  return Object.freeze({ scheme, host, port, path: `/${segments.join('/')}` });
}

export function endpointText(endpoint: TransportEndpoint | null | undefined): string | null {
  return endpoint ? `${endpoint.scheme}://${endpoint.host}:${endpoint.port}${endpoint.path}` : null;
}

/** A WebSocket close reason (at most 123 bytes by protocol) reduced to safe, printable text. */
export function sanitizeCloseReason(reason: unknown): string {
  if (typeof reason !== 'string' || !reason) return '';
  return reason
    .slice(0, 123)
    .replace(/[^\x20-\x7e]+/g, ' ')
    .replace(/\bsips?:\S+/gi, '[sip address]')
    .replace(/\b(?:\d{1,3}\.){3}\d{1,3}(?::\d+)?\b/g, '[address]')
    .replace(/\b(bearer|token|password|passwd|secret|authorization|cookie|nonce)\b\s*[:=]?\s*\S*/gi, '$1 [removed]')
    .replace(/\beyJ[A-Za-z0-9_-]*/g, '[removed]')
    .replace(/[A-Za-z0-9+/_=-]{20,}/g, '[removed]')
    // 12+ characters mixing letters and digits: an ID or a token fragment, not a word.
    .replace(/\b(?=[A-Za-z0-9_-]*\d)(?=[A-Za-z0-9_-]*[A-Za-z])[A-Za-z0-9_-]{12,}\b/g, '[removed]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

/** The negotiated subprotocol, as a short token. */
export function socketProtocol(protocol: unknown): string {
  return typeof protocol === 'string' && /^[A-Za-z0-9._-]{0,32}$/.test(protocol) ? protocol : 'other';
}

interface Attempt {
  attempt: number;
  via: 'connect' | 'register';
  endpoint: TransportEndpoint | null;
  startedAt: number;
  openedAt: number | null;
  endedAt: number | null;
  connectMs: number | null;
  outcome: TransportAttempt['outcome'];
  socket: number | null;
  socketCreated: boolean | null;
  errorSeen: boolean;
  timedOut: boolean;
  close: TransportClose | null;
  csp: TransportCspViolation | null;
  sipStatus: number | null;
  failure: TransportFailureKind | null;
  failureCode: string | null;
  view: TransportAttempt | null;
}

const REGISTER_FAILURES: Readonly<Record<string, TransportFailureKind>> = { register_rejected: 'register_rejected', register_timeout: 'register_timeout', register_failed: 'register_failed' };
const NOT_CREATED_CODES = new Set(['invalid_aor', 'transport_create_failed']);

function classify(a: Attempt): TransportFailureKind {
  const register = a.failureCode ? REGISTER_FAILURES[a.failureCode] : undefined;
  if (register) return register;
  if (a.csp?.disposition === 'enforce' && a.openedAt === null) return 'blocked_by_csp';
  if (a.socketCreated === false || (a.failureCode !== null && NOT_CREATED_CODES.has(a.failureCode))) return 'socket_not_created';
  if (a.timedOut && a.openedAt === null) return 'connect_timeout';
  if (a.via === 'connect' && a.openedAt === null) return 'handshake_failed';
  if (a.close) return a.close.was_clean && a.close.code !== 1006 ? 'closed_by_server' : 'connection_lost';
  if (a.failureCode === 'transport_error' || a.failureCode === 'transport_closed') return 'connection_lost';
  return 'transport_error';
}

const iso = (ms: number | null) => (ms === null ? null : new Date(ms).toISOString());

/** Engine-side aggregator. Every mutation returns the log entry to emit, or null when nothing changed. */
export class TransportRecorder {
  private sequence = 0;
  private endpoint: TransportEndpoint | null = null;
  private current: Attempt | null = null;
  private lastFailure: Attempt | null = null;
  private readonly attempts: Attempt[] = [];
  private consecutive = 0;
  private retryAt: number | null = null;
  private retryDelay: number | null = null;
  private openSocket: number | null = null;
  private readonly log: TransportLogEntry[] = [];
  private cached: TransportDiagnostic | null = null;

  constructor(private readonly now: () => number, private readonly limit = TRANSPORT_LOG_LIMIT) {}

  /** The engine is about to connect, or to REGISTER again on a socket that is already open. */
  begin(websocketUrl: string, attempt: number, via: 'connect' | 'register'): TransportLogEntry {
    this.endpoint = transportEndpoint(websocketUrl);
    const a: Attempt = {
      attempt, via, endpoint: this.endpoint, startedAt: this.now(), openedAt: null, endedAt: null, connectMs: null, outcome: via === 'register' ? 'open' : 'pending',
      socket: via === 'register' ? this.openSocket : null, socketCreated: via === 'register' ? true : null, errorSeen: false, timedOut: false,
      close: null, csp: null, sipStatus: null, failure: null, failureCode: null, view: null,
    };
    this.current = a;
    this.attempts.push(a);
    if (this.attempts.length > KEEP_ATTEMPTS) this.attempts.shift();
    this.retryAt = null;
    this.retryDelay = null;
    return this.entry(via === 'connect' ? 'connect_started' : 'register_started', a);
  }

  observe(o: SipSocketObservation): TransportLogEntry | null {
    const now = this.now();
    if (o.kind === 'csp_violation') {
      const late = this.lastFailure && this.lastFailure.endedAt !== null && now - this.lastFailure.endedAt <= LATE_CSP_MS ? this.lastFailure : null;
      const a = this.current && this.current.outcome !== 'failed' ? this.current : late;
      if (a) { a.csp = { directive: 'connect-src', blocked: o.blocked, disposition: o.disposition }; this.reclassify(a); }
      return this.entry('csp_violation', a);
    }
    if (o.kind === 'socket_created') {
      const a = this.current;
      if (a && a.via === 'connect' && a.socket === null && a.outcome === 'pending') { a.socket = o.socket; a.socketCreated = true; this.touch(a); return null; }
      // A socket the engine did not ask for; record it as its own attempt so nothing is invisible.
      const orphan = this.begin(endpointText(this.endpoint) ?? '', this.current?.attempt ?? 1, 'connect');
      if (this.current) { this.current.socket = o.socket; this.current.socketCreated = true; }
      return orphan;
    }
    const a = this.bySocket(o.socket) ?? (o.kind === 'socket_not_created' ? this.current : null);
    if (!a) return null;
    switch (o.kind) {
      case 'socket_not_created':
        a.socketCreated = false;
        a.connectMs ??= now - a.startedAt;
        break;
      case 'socket_open':
        a.openedAt = now;
        a.connectMs = now - a.startedAt;
        if (a.outcome === 'pending') a.outcome = 'open';
        this.openSocket = o.socket;
        break;
      case 'socket_error':
        if (a.errorSeen) return null;
        a.errorSeen = true;
        break;
      case 'socket_closed':
        a.close = Object.freeze({ code: o.code, reason: sanitizeCloseReason(o.reason), was_clean: o.was_clean });
        if (a.openedAt === null) a.connectMs ??= now - a.startedAt;
        if (this.openSocket === o.socket) this.openSocket = null;
        break;
      case 'connect_timeout':
        if (a.timedOut) return null;
        a.timedOut = true;
        break;
    }
    this.reclassify(a);
    return this.entry(o.kind, a);
  }

  /** The engine handled a failure of the current attempt (connect rejected, REGISTER failed, socket dropped). */
  fail(failure: SipFailure): TransportLogEntry | null {
    let a = this.current;
    if (!a) { this.begin(endpointText(this.endpoint) ?? '', 1, 'connect'); a = this.current!; }
    if (a.outcome === 'failed') return null;
    const now = this.now();
    a.outcome = 'failed';
    a.endedAt = now;
    a.failureCode = failure.code;
    if (failure.statusCode !== undefined) a.sipStatus = failure.statusCode;
    if (a.via === 'connect' && a.openedAt === null) a.connectMs ??= now - a.startedAt;
    a.failure = classify(a);
    this.consecutive += 1;
    this.lastFailure = a;
    return this.entry('attempt_failed', a);
  }

  registered(): TransportLogEntry | null {
    const a = this.current;
    if (!a) return null;
    a.outcome = 'registered';
    a.endedAt = this.now();
    this.consecutive = 0;
    this.retryAt = null;
    this.retryDelay = null;
    return this.entry('registered', a);
  }

  retry(delayMs: number): TransportLogEntry {
    this.retryAt = this.now() + delayMs;
    this.retryDelay = delayMs;
    return this.entry('retry_scheduled', this.current, delayMs);
  }

  snapshot(): TransportDiagnostic {
    if (this.cached) return this.cached;
    this.cached = Object.freeze({
      endpoint: this.endpoint,
      current: this.current ? this.view(this.current) : null,
      last_failure: this.lastFailure ? this.view(this.lastFailure) : null,
      consecutive_failures: this.consecutive,
      next_retry_at: iso(this.retryAt),
      retry_delay_ms: this.retryDelay,
      recent: Object.freeze([...this.log]),
    });
    return this.cached;
  }

  private bySocket(socket: number): Attempt | null {
    for (let i = this.attempts.length - 1; i >= 0; i--) if (this.attempts[i]!.socket === socket) return this.attempts[i]!;
    return null;
  }

  private reclassify(a: Attempt): void {
    if (a.outcome === 'failed') a.failure = classify(a);
    this.touch(a);
  }

  private touch(a: Attempt): void {
    a.view = null;
    this.cached = null;
  }

  private view(a: Attempt): TransportAttempt {
    a.view ??= Object.freeze({
      attempt: a.attempt,
      via: a.via,
      endpoint: a.endpoint,
      started_at: new Date(a.startedAt).toISOString(),
      opened_at: iso(a.openedAt),
      ended_at: iso(a.endedAt),
      connect_ms: a.connectMs,
      outcome: a.outcome,
      socket_created: a.socketCreated,
      error_seen: a.errorSeen,
      timed_out: a.timedOut,
      close: a.close,
      csp_violation: a.csp,
      sip_status: a.sipStatus,
      failure: a.failure,
      failure_code: a.failureCode,
    });
    return a.view;
  }

  private entry(kind: TransportEventKind, a: Attempt | null, retryInMs: number | null = null): TransportLogEntry {
    if (a) this.touch(a);
    this.cached = null;
    const entry: TransportLogEntry = Object.freeze({
      sequence: ++this.sequence,
      at: new Date(this.now()).toISOString(),
      kind,
      attempt: a?.attempt ?? 0,
      failure: kind === 'attempt_failed' || kind === 'socket_closed' || kind === 'csp_violation' ? a?.failure ?? null : null,
      close: kind === 'socket_closed' ? a?.close ?? null : null,
      sip_status: kind === 'attempt_failed' ? a?.sipStatus ?? null : null,
      connect_ms: kind === 'socket_open' || kind === 'socket_closed' || kind === 'socket_not_created' || kind === 'attempt_failed' ? a?.connectMs ?? null : null,
      retry_in_ms: retryInMs,
    });
    this.log.push(entry);
    if (this.log.length > this.limit) this.log.splice(0, this.log.length - this.limit);
    return entry;
  }
}
